import styled from "styled-components"

import theme from "theme"
import { Styles } from "typings/react-select"

type SelectStyles = {
  error?: boolean
  optionFontStyles: Record<string, any>
  size?: "sm" | "md"
  placeholderFontSize?: number
}

export const customReactSelectStyles = ({
  error,
  optionFontStyles,
  size = "md",
  placeholderFontSize
}: SelectStyles): Styles => ({
  container: styles => ({
    ...styles,
    width: "100%",
    borderColor: error ? theme.colors.salmon : theme.colors.evergreen,
    backgroundColor: error ? theme.colors.lightSalmon : "transparent",
    webkitFontSmoothing: "antialiased",
    fontSize: 14,
    lineHeight: 1.35,
    ...optionFontStyles
  }),

  placeholder: (styles, { hasValue, selectProps }) => ({
    ...styles,
    fontFamily: theme.fonts.mono,
    textTransform: "uppercase",
    fontSize: hasValue || selectProps.inputValue ? 9 : placeholderFontSize ?? 12,
    color: error ? theme.colors.salmon : theme.colors.silverDark,
    position: "absolute",
    transform: hasValue || selectProps.inputValue ? "translateY(-155%)" : "translateY(-50%)",
    padding: 0,
    transition: "top 0.2s, font-size 0.2s"
  }),

  menu: styles => ({
    ...styles,
    maxHeight: 280,
    marginTop: -1,
    width: "100%",
    boxSizing: "border-box",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    border: `1px solid ${theme.colors.evergreen}`,
    boxShadow: "none"
  }),

  menuList: styles => ({
    ...styles,
    textAlign: "left",
    maxHeight: 250,
    padding: 0
  }),

  control: (styles, { isFocused, isDisabled, menuIsOpen, hasValue, isMulti }) => {
    let padding: string = theme.space[0]

    if (isMulti) {
      if (hasValue) {
        /* The selected 'tag' has the vertical padding so no need for it here */
        padding = `${theme.space[1]} ${theme.space[3]}`
      } else {
        padding = `${theme.space[1]} ${theme.space[3]}`
      }
    } else if (size === "sm") {
      padding = `${theme.space[2]} ${theme.space[3]}`
    } else {
      padding = `${theme.space[4]} ${theme.space[5]}`
    }

    return {
      ...styles,
      width: "100%",
      padding,
      color: error ? theme.colors.salmon : theme.colors.white,
      backgroundColor: error ? theme.colors.lightSalmon : "transparent",
      borderWidth: "1px",
      borderColor: `${
        error ? theme.colors.salmon : isFocused ? theme.colors.melrose : theme.colors.evergreen
      } !important`,
      borderBottomLeftRadius: menuIsOpen ? 0 : 4,
      borderBottomRightRadius: menuIsOpen ? 0 : 4,
      overflowX: "overlay" as any,
      pointerEvents: !isDisabled ? "auto" : undefined,
      boxShadow: "none"
    }
  },

  indicatorSeparator: () => ({
    display: "none"
  }),

  indicatorsContainer: styles => ({
    ...styles,
    display: "flex"
  }),

  dropdownIndicator: (styles, { isFocused, isDisabled }) => ({
    ...styles,
    display: isDisabled ? "none" : "initial",
    padding: 1,
    color: error ? theme.colors.salmon : theme.colors.evergreen,
    transform: error ? "translateX(-15px)" : isFocused ? "rotateX(180deg)" : "none"
  }),

  groupHeading: styles => ({
    ...styles,
    fontFamily: theme.fonts.mono,
    fontSize: 12,
    letterSpacing: 1.6,
    padding: theme.space[3],
    color: theme.colors.evergreen
  }),

  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    boxSizing: "border-box",
    padding: size === "sm" ? `${theme.space[2]} ${theme.space[3]}` : `${theme.space[3]} ${theme.space[5]}`,
    backgroundColor: isSelected || isFocused ? theme.colors.paleAqua : theme.colors.white,
    color: theme.colors.evergreen
  }),

  valueContainer: (styles, { error }) => ({
    ...styles,
    overflow: "initial",
    color: error ? theme.colors.salmon : theme.colors.evergreen,
    padding: 0
  }),
  singleValue: (styles, { isDisabled }) => ({
    ...styles,
    color: error ? theme.colors.salmon : theme.colors.evergreen,
    overflow: !isDisabled ? "hidden" : undefined
  }),

  multiValue: styles => ({
    ...styles,
    backgroundColor: theme.colors.paleAqua,
    padding: theme.space[2]
  }),
  multiValueLabel: styles => ({
    ...styles,
    color: theme.colors.evergreen,
    fontSize: "inherit",
    padding: 0
  }),
  multiValueRemove: styles => ({
    ...styles,
    color: theme.colors.evergreen,
    ":hover": {
      opacity: theme.reducedOpacity
    }
  })
})

export const HTMLSelectContainer = styled.div<{ error?: boolean }>`
  position: relative;
  width: 100%;
`

export const HTMLSelect = styled.select<{ error?: boolean }>`
  width: 100%;
  max-height: 260px;
  padding: ${props => props.theme.space[4]} ${theme.space[5]};
  outline: none;

  color: ${props => (props.error ? props.theme.colors.salmon : props.theme.colors.evergreen)};
  background-color: ${props => (props.error ? props.theme.colors.lightSalmon : props.theme.colors.white)};

  border-width: 1px;
  border-color: ${props => (props.error ? props.theme.colors.salmon : props.theme.colors.evergreen)};
  border-radius: ${props => props.theme.space[1]};

  font-family: ${props => props.theme.fonts.paragraph};
  font-size: 14px;
  -webkit-font-smoothing: antialiased;

  /* https://stackoverflow.com/questions/7638677/how-can-i-remove-the-gloss-on-a-select-element-in-safari-on-mac */
  background: url(data:image/svg+xml;base64,PHN2ZyBpZD0iTGF5ZXJfMSIgZGF0YS1uYW1lPSJMYXllciAxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0Ljk1IDEwIj48ZGVmcz48c3R5bGU+LmNscy0xe2ZpbGw6I2ZmZjt9LmNscy0ye2ZpbGw6IzQ0NDt9PC9zdHlsZT48L2RlZnM+PHRpdGxlPmFycm93czwvdGl0bGU+PHJlY3QgY2xhc3M9ImNscy0xIiB3aWR0aD0iNC45NSIgaGVpZ2h0PSIxMCIvPjxwb2x5Z29uIGNsYXNzPSJjbHMtMiIgcG9pbnRzPSIxLjQxIDQuNjcgMi40OCAzLjE4IDMuNTQgNC42NyAxLjQxIDQuNjciLz48cG9seWdvbiBjbGFzcz0iY2xzLTIiIHBvaW50cz0iMy41NCA1LjMzIDIuNDggNi44MiAxLjQxIDUuMzMgMy41NCA1LjMzIi8+PC9zdmc+)
    no-repeat 95% 50%;
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;
  &::-ms-expand {
    display: none;
  }

  /* Removes default arrow from firefox */
  text-overflow: "";
  text-indent: 0.01px;

  /* Empty value instead of a placeholder for <select> */
  option[value=""][disabled] {
    display: none;
  }

  &:focus {
    border-color: ${props => props.theme.colors.melrose};
  }
`
